// Slack-style UI tones, synthesized with WebAudio (no files to load, works offline). Volume/enablement come from prefs.
import { analytics } from "./analytics";

let ctx = null;
let master = null;
let settings = { enabled: true, volume: 70, huddles: true, sent: false, notification: "knock" };

const ensure = () => {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.connect(ctx.destination);
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  master.gain.value = Math.pow(Math.max(0, Math.min(100, settings.volume)) / 100, 1.6);
  return ctx;
};

export const configureSounds = (prefs) => {
  settings = { enabled: prefs?.sound !== false, volume: prefs?.sounds?.volume ?? 70, huddles: prefs?.sounds?.huddles !== false, sent: !!prefs?.sounds?.sent, notification: prefs?.sounds?.notification || "knock" };
};

// Unlock audio on the first user gesture so later programmatic tones are allowed.
export const unlockAudio = () => { try { ensure(); } catch { /* ignore */ } };
if (typeof window !== "undefined") ["pointerdown", "keydown", "touchstart"].forEach((ev) => window.addEventListener(ev, unlockAudio, { once: true, passive: true }));

const tone = (c, { freq, at = 0, dur = 0.18, type = "sine", gain = 0.5, slideTo = null, attack = 0.01 }) => {
  const o = c.createOscillator();
  const g = c.createGain();
  const t = c.currentTime + at;
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(gain, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g).connect(master);
  o.start(t);
  o.stop(t + dur + 0.05);
};

const noise = (c, { at = 0, dur = 0.25, from = 400, to = 4000, gain = 0.25 }) => {
  const len = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = "bandpass";
  f.Q.value = 1.2;
  const t = c.currentTime + at;
  f.frequency.setValueAtTime(from, t);
  f.frequency.exponentialRampToValueAtTime(to, t + dur);
  const g = c.createGain();
  g.gain.setValueAtTime(gain, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(f).connect(g).connect(master);
  src.start(t);
};

const SOUNDS = {
  // huddles
  huddle_join: (c) => { tone(c, { freq: 523.25, dur: 0.16, gain: 0.45 }); tone(c, { freq: 783.99, at: 0.12, dur: 0.28, gain: 0.45 }); },
  huddle_leave: (c) => { tone(c, { freq: 783.99, dur: 0.16, gain: 0.4 }); tone(c, { freq: 523.25, at: 0.12, dur: 0.3, gain: 0.4 }); },
  huddle_start: (c) => { [523.25, 659.25, 783.99].forEach((f, i) => tone(c, { freq: f, at: i * 0.11, dur: 0.22, gain: 0.42 })); tone(c, { freq: 1046.5, at: 0.34, dur: 0.4, gain: 0.35 }); },
  huddle_end: (c) => { [783.99, 659.25, 523.25].forEach((f, i) => tone(c, { freq: f, at: i * 0.12, dur: 0.24, gain: 0.4 })); },
  participant_join: (c) => tone(c, { freq: 880, dur: 0.14, gain: 0.3, type: "triangle" }),
  participant_leave: (c) => tone(c, { freq: 587.33, dur: 0.18, gain: 0.3, type: "triangle" }),
  mute: (c) => tone(c, { freq: 440, dur: 0.08, gain: 0.35, type: "square", slideTo: 300 }),
  unmute: (c) => tone(c, { freq: 440, dur: 0.08, gain: 0.35, type: "square", slideTo: 620 }),
  // messaging
  message_sent: (c) => noise(c, { dur: 0.22, from: 600, to: 5000, gain: 0.2 }),
  // notification variants
  knock: (c) => { tone(c, { freq: 880, dur: 0.35, gain: 0.45 }); tone(c, { freq: 1174.66, at: 0.09, dur: 0.35, gain: 0.45 }); },
  ding: (c) => { tone(c, { freq: 1318.5, dur: 0.6, gain: 0.4 }); tone(c, { freq: 2637, dur: 0.3, gain: 0.12 }); },
  hi: (c) => { [659.25, 830.61, 987.77].forEach((f, i) => tone(c, { freq: f, at: i * 0.1, dur: 0.25, gain: 0.4 })); },
  boing: (c) => tone(c, { freq: 220, dur: 0.45, gain: 0.45, type: "triangle", slideTo: 660 }),
};

export const SOUND_NAMES = Object.keys(SOUNDS);
export const NOTIFICATION_SOUNDS = [["knock", "Knock brush"], ["ding", "Ding"], ["hi", "Hi there"], ["boing", "Boing"]];

export const playSound = (name, { force = false } = {}) => {
  if (!force && !settings.enabled) return false;
  if (!force && name.startsWith("huddle_") && !settings.huddles) return false;
  if (!force && (name === "participant_join" || name === "participant_leave" || name === "mute" || name === "unmute") && !settings.huddles) return false;
  if (!force && name === "message_sent" && !settings.sent) return false;
  try {
    const c = ensure();
    if (!c || !SOUNDS[name]) return false;
    SOUNDS[name](c);
    return true;
  } catch { return false; }
};

export const playNotification = () => playSound(settings.notification) && analytics.track("sound_played", { category: "notifications", label: settings.notification });
export const previewSound = (name) => playSound(name, { force: true });
